import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  Volume2,
  VolumeX,
  Megaphone,
  MegaphoneOff,
  Gem,
  ArrowRight,
} from "lucide-react";
import { Card } from "@/types/linguadeck";
import { useGamification } from "@/contexts/GamificationContext";
import { playAudio } from "@/lib/audioUtils";
import { useSoundStore } from "@/stores/useSoundStore";
import { playPop, playSuccess, playError } from "@/lib/studySfx";

const CRYSTALS_PER_CORRECT = 8;
const MIN_BUBBLES = 4;
const MAX_BUBBLES = 6;
const FLOAT_DURATION_MIN = 18;
const FLOAT_DURATION_MAX = 28;

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Floating +N crystals animation. */
function FloatingCrystals({
  amount,
  onComplete,
}: {
  amount: number;
  onComplete: () => void;
}) {
  return (
    <motion.div
      className="pointer-events-none fixed inset-0 flex items-center justify-center z-50"
      initial={false}
    >
      <motion.span
        className="inline-flex items-center gap-1.5 text-2xl font-bold text-[hsl(var(--accent2))] drop-shadow-lg whitespace-nowrap"
        initial={{ opacity: 1, y: 0, scale: 1 }}
        animate={{ opacity: 0, y: -80, scale: 1.2 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        onAnimationComplete={onComplete}
      >
        +{amount}
        <Gem className="w-6 h-6" aria-hidden />
      </motion.span>
    </motion.div>
  );
}

/** Pulse wave that radiates from a bubble when clicked (scanning for sound). */
function PulseWave({ onComplete }: { onComplete: () => void }) {
  return (
    <motion.div
      className="absolute inset-0 rounded-full border-2 border-white/50 pointer-events-none"
      initial={{ scale: 0.5, opacity: 0.8 }}
      animate={{ scale: 2.5, opacity: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      onAnimationComplete={onComplete}
    />
  );
}

/** Single floating bubble with glassmorphism and blob shape. */
function SoundBubble({
  term,
  termLang,
  isCorrect,
  isPopping,
  isWrong,
  showPulse,
  onPulseComplete,
  onHoverStart,
  onHoverEnd,
  onClick,
  index,
  total,
}: {
  term: string;
  termLang: string;
  isCorrect: boolean;
  isPopping: boolean;
  isWrong: boolean;
  showPulse: boolean;
  onPulseComplete: () => void;
  onHoverStart: () => void;
  onHoverEnd: () => void;
  onClick: () => void;
  index: number;
  total: number;
}) {
  const duration = useMemo(
    () =>
      FLOAT_DURATION_MIN +
      Math.random() * (FLOAT_DURATION_MAX - FLOAT_DURATION_MIN),
    []
  );
  const xOffset = useMemo(
    () => (index % 2 === 0 ? 1 : -1) * (15 + Math.random() * 25),
    [index]
  );
  const yOffset = useMemo(
    () => (index % 3 === 0 ? 1 : -1) * (12 + Math.random() * 20),
    [index]
  );

  const angle = (2 * Math.PI * index) / total - Math.PI / 2;
  const radiusPct = 32;
  const centerPctX = 50;
  const centerPctY = 42;
  const leftPct = centerPctX + radiusPct * Math.cos(angle);
  const topPct = centerPctY + radiusPct * Math.sin(angle);

  const size = 72 + (index % 3) * 12;
  const bubbleStyle = {
    width: size,
    height: size,
    left: `calc(${leftPct}% - ${size / 2}px)`,
    top: `calc(${topPct}% - ${size / 2}px)`,
  };

  if (isPopping) {
    return (
      <motion.div
        className="absolute pointer-events-none rounded-[40%_60%_70%_30%_/_40%_50%_60%_50%]"
        style={{
          ...bubbleStyle,
          background: "rgba(168, 85, 247, 0.25)",
          boxShadow: "0 0 40px rgba(168, 85, 247, 0.5)",
        }}
        initial={{ scale: 1, opacity: 1 }}
        animate={{
          scale: 2.5,
          opacity: 0,
        }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      />
    );
  }

  return (
    <motion.div
      className="absolute cursor-pointer select-none touch-manipulation"
      style={bubbleStyle}
      initial={false}
      animate={{
        x: [0, xOffset, -xOffset * 0.6, 0],
        y: [0, yOffset * 0.8, -yOffset * 0.5, 0],
      }}
      transition={{
        type: "tween",
        duration,
        repeat: Infinity,
        repeatType: "reverse",
      }}
      onHoverStart={onHoverStart}
      onHoverEnd={onHoverEnd}
      onClick={onClick}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.98 }}
    >
      <motion.div
        className="w-full h-full flex items-center justify-center border border-white/30 overflow-hidden"
        style={{
          borderRadius: "40% 60% 70% 30% / 40% 50% 60% 50%",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: isWrong
            ? "0 0 24px rgba(239, 68, 68, 0.5), inset 0 1px 0 rgba(255,255,255,0.2)"
            : "0 0 24px rgba(168, 85, 247, 0.25), inset 0 1px 0 rgba(255,255,255,0.2)",
        }}
        animate={
          isWrong
            ? {
                x: [0, -6, 6, -6, 6, 0],
                transition: { duration: 0.4 },
              }
            : {
                borderRadius: [
                  "40% 60% 70% 30% / 40% 50% 60% 50%",
                  "60% 40% 30% 70% / 60% 30% 70% 40%",
                  "40% 60% 70% 30% / 40% 50% 60% 50%",
                ],
                transition: { duration: 8, repeat: Infinity, repeatType: "reverse" },
              }
        }
      >
        {showPulse && <PulseWave onComplete={onPulseComplete} />}
        <span className="text-lg font-semibold text-white/90 drop-shadow-md px-2 text-center line-clamp-2 relative z-10">
          ?
        </span>
      </motion.div>
    </motion.div>
  );
}

/** Tutorial arrow pointing from center toward a bubble (bouncing). */
function TutorialArrow({ angleDeg, delay }: { angleDeg: number; delay: number }) {
  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        left: "50%",
        top: "42%",
        width: 28,
        height: 28,
        marginLeft: -14,
        marginTop: -14,
        transform: `rotate(${angleDeg}deg)`,
        transformOrigin: "center center",
      }}
      initial={{ opacity: 0, x: 0 }}
      animate={{
        opacity: 1,
        x: [0, 6, 0],
      }}
      transition={{
        opacity: { duration: 0.4, delay },
        x: {
          duration: 1.2,
          delay: delay + 0.3,
          repeat: Infinity,
          repeatType: "reverse",
        },
      }}
    >
      <ArrowRight className="w-7 h-7 text-white/70 drop-shadow-md" strokeWidth={2.5} />
    </motion.div>
  );
}

/** Particle burst for pop effect (simple circles). */
function PopParticles() {
  const particles = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        id: i,
        angle: (i / 12) * 360,
        delay: i * 0.02,
      })),
    []
  );

  return (
    <div className="pointer-events-none fixed inset-0 flex items-center justify-center z-40">
      {particles.map(({ id, angle, delay }) => (
        <motion.div
          key={id}
          className="absolute w-2 h-2 rounded-full bg-[hsl(var(--accent2))] opacity-90"
          initial={{ scale: 1, x: 0, y: 0, opacity: 1 }}
          animate={{
            scale: 0,
            x: Math.cos((angle * Math.PI) / 180) * 60,
            y: Math.sin((angle * Math.PI) / 180) * 60,
            opacity: 0,
          }}
          transition={{
            delay,
            duration: 0.4,
            ease: "easeOut",
          }}
        />
      ))}
    </div>
  );
}

interface SoundCloudsModeProps {
  cards: Card[];
  lang?: string;
  onBack?: () => void;
  onProgressComplete?: (pct: number) => void;
}

export default function SoundCloudsMode({
  cards,
  lang: deckLang,
  onBack,
}: SoundCloudsModeProps) {
  const { t } = useTranslation();
  const { addCrystals, finishStudySession } = useGamification();
  const {
    isSoundEnabled,
    isVoiceEnabled,
    gameVolume,
    voiceVolume,
    setGameVolume,
    setVoiceVolume,
    setVoiceEnabled,
    toggleSound,
    toggleVoice,
  } = useSoundStore();

  const filteredCards = useMemo(
    () =>
      cards.filter((c) => (c.front || "").trim() && (c.back || "").trim()),
    [cards]
  );

  const [targetCard, setTargetCard] = useState<Card | null>(null);
  const [bubbleCards, setBubbleCards] = useState<Card[]>([]);
  const [correctIndex, setCorrectIndex] = useState<number>(0);
  const [poppingIndex, setPoppingIndex] = useState<number | null>(null);
  const [wrongIndex, setWrongIndex] = useState<number | null>(null);
  const [floatingCrystals, setFloatingCrystals] = useState<number | null>(null);
  const [sessionCrystals, setSessionCrystals] = useState(0);
  const [showPopParticles, setShowPopParticles] = useState(false);
  const [showTutorial, setShowTutorial] = useState(true);
  const [pulsingBubbleIndex, setPulsingBubbleIndex] = useState<number | null>(null);
  const popEndTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tutorialTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pulseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentlySpeakingWordRef = useRef<string | null>(null);

  useEffect(() => {
    tutorialTimeoutRef.current = setTimeout(() => {
      setShowTutorial(false);
      tutorialTimeoutRef.current = null;
    }, 5000);
    return () => {
      if (tutorialTimeoutRef.current) clearTimeout(tutorialTimeoutRef.current);
    };
  }, []);

  const pickNewRound = useCallback(() => {
    if (filteredCards.length === 0) return;
    const shuffled = shuffleArray(filteredCards);
    const target = shuffled[0];
    const count = Math.min(MAX_BUBBLES, Math.max(2, filteredCards.length));
    const pool = shuffled.slice(1);
    const wrongs: Card[] = [];
    for (let i = 0; i < count - 1 && pool.length > 0; i++) {
      wrongs.push(pool[i % pool.length]);
    }
    const bubbleSet = shuffleArray([target, ...wrongs]);
    const correctIdx = bubbleSet.findIndex((c) => c.id === target?.id);
    setTargetCard(target ?? null);
    setBubbleCards(bubbleSet);
    setCorrectIndex(correctIdx >= 0 ? correctIdx : 0);
    setPoppingIndex(null);
    setWrongIndex(null);
  }, [filteredCards]);

  useEffect(() => {
    pickNewRound();
    return () => {
      if (popEndTimeoutRef.current) clearTimeout(popEndTimeoutRef.current);
      if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
    };
  }, [pickNewRound]);

  const handleBubbleHoverStart = useCallback(
    (term: string, termLang: string, card: Card) => {
      if (!isVoiceEnabled || !term?.trim() || !card) return;
      currentlySpeakingWordRef.current = term;
      const wordForThisUtterance = term;
      void playAudio({
        card,
        side: "front",
        deckLang: termLang ?? deckLang,
        volume: voiceVolume,
        onEnd: () => {
          if (currentlySpeakingWordRef.current === wordForThisUtterance) {
            currentlySpeakingWordRef.current = null;
          }
        },
      });
    },
    [isVoiceEnabled, voiceVolume, deckLang]
  );

  const handleBubbleClick = useCallback(
    (index: number, card: Card) => {
      if (poppingIndex !== null || wrongIndex !== null) return;
      setShowTutorial(false);
      if (tutorialTimeoutRef.current) {
        clearTimeout(tutorialTimeoutRef.current);
        tutorialTimeoutRef.current = null;
      }
      setPulsingBubbleIndex(index);
      if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
      pulseTimeoutRef.current = setTimeout(() => {
        setPulsingBubbleIndex(null);
        pulseTimeoutRef.current = null;
      }, 650);
      if (isVoiceEnabled && (card.front || "").trim()) {
        void playAudio({
          card,
          side: "front",
          deckLang: deckLang,
          volume: voiceVolume,
        });
      }
      const correct = targetCard && card.id === targetCard.id;
      if (correct) {
        if (isSoundEnabled) playPop(gameVolume * 0.5);
        setPoppingIndex(index);
        setShowPopParticles(true);
        if (isSoundEnabled) playSuccess(gameVolume * 0.7);
        const amount = CRYSTALS_PER_CORRECT;
        const result = addCrystals(amount, t("soundClouds.rewardReason"), {
          isLearningAction: true,
          silent: true,
        });
        const totalAdd = typeof result === "object" ? result.totalAdd : amount;
        setFloatingCrystals(totalAdd);
        setSessionCrystals((prev) => prev + totalAdd);
        finishStudySession(1, false);
        popEndTimeoutRef.current = setTimeout(() => {
          setShowPopParticles(false);
          setPoppingIndex(null);
          pickNewRound();
          popEndTimeoutRef.current = null;
        }, 600);
      } else {
        if (isSoundEnabled) playError(gameVolume * 0.8);
        setWrongIndex(index);
        const wrongTimer = setTimeout(() => {
          setWrongIndex(null);
        }, 600);
        return () => clearTimeout(wrongTimer);
      }
    },
    [
      targetCard,
      poppingIndex,
      wrongIndex,
      isSoundEnabled,
      isVoiceEnabled,
      gameVolume,
      voiceVolume,
      deckLang,
      addCrystals,
      finishStudySession,
      t,
      pickNewRound,
    ]
  );

  const headerRow = (
    <header className="flex justify-between items-center w-full max-w-2xl mx-auto mb-6 gap-4">
      <div className="min-w-0 flex-1">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] font-medium transition-colors"
            aria-label={t("studyMenu.backToDeck")}
          >
            <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
            {t("studyMenu.backToDeck")}
          </button>
        ) : (
          <div />
        )}
      </div>
      <div className="grid grid-cols-[64px_auto] gap-x-2 gap-y-1 items-center shrink-0">
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={gameVolume}
          onChange={(e) => setGameVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.gameVolume")}
        />
        <button
          type="button"
          onClick={toggleSound}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isSoundEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] opacity-50"}`}
          aria-label={isSoundEnabled ? t("common.muteMusic") : t("common.unmuteMusic")}
        >
          {isSoundEnabled ? (
            <Volume2 className="w-5 h-5" />
          ) : (
            <VolumeX className="w-5 h-5" />
          )}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={voiceVolume}
          onChange={(e) => setVoiceVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.voiceVolume")}
        />
        <button
          type="button"
          onClick={toggleVoice}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isVoiceEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] opacity-50"}`}
          aria-label={isVoiceEnabled ? t("common.voiceModeOn") : t("common.voiceModeOff")}
        >
          {isVoiceEnabled ? (
            <Megaphone className="w-5 h-5" />
          ) : (
            <MegaphoneOff className="w-5 h-5" />
          )}
        </button>
      </div>
    </header>
  );

  if (filteredCards.length === 0) {
    return (
      <div className="flex flex-col w-full max-w-lg mx-auto px-4">
        {headerRow}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-8 text-center">
          <p className="text-lg font-medium text-[hsl(var(--foreground))]">
            {t("soundClouds.needCards")}
          </p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            {t("soundClouds.addMoreCards")}
          </p>
        </div>
      </div>
    );
  }

  const targetTranslation = targetCard?.back?.trim() ?? "";

  return (
    <div className="flex flex-col w-full max-w-4xl mx-auto px-4 min-h-[calc(100vh-6rem)]">
      {headerRow}

      <div
        className="relative flex-1 rounded-3xl overflow-hidden min-h-[420px] flex flex-col items-center justify-center"
        style={{
          background:
            "linear-gradient(160deg, #0f0a1a 0%, #1a0f2e 35%, #0d0b1a 70%, #15102a 100%)",
          boxShadow: "inset 0 0 80px rgba(88, 28, 135, 0.15)",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            background:
              "radial-gradient(ellipse 80% 50% at 50% 20%, rgba(168, 85, 247, 0.2) 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 80% 80%, rgba(236, 72, 153, 0.12) 0%, transparent 50%)",
          }}
        />

        <AnimatePresence mode="wait">
          {showPopParticles && <PopParticles />}
        </AnimatePresence>

        {floatingCrystals !== null && (
          <FloatingCrystals
            amount={floatingCrystals}
            onComplete={() => setFloatingCrystals(null)}
          />
        )}

        <div className="relative w-full flex-1 flex items-center justify-center" style={{ minHeight: 320 }}>
          <AnimatePresence>
            {showTutorial && (
              <motion.div
                className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <motion.div
                  className="mx-4 max-w-md rounded-xl border border-white/20 bg-white/10 p-4 text-center shadow-lg backdrop-blur-md"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                >
                  <p className="text-lg font-medium text-white sm:text-xl">
                    {t("soundClouds.tutorialClickClouds")}
                  </p>
                </motion.div>
                {bubbleCards.map((_, index) => {
                  const total = bubbleCards.length;
                  const angle = (2 * Math.PI * index) / total - Math.PI / 2;
                  const radiusPct = 32;
                  const leftPct = 50 + radiusPct * Math.cos(angle);
                  const topPct = 42 + radiusPct * Math.sin(angle);
                  const angleDeg = (Math.atan2(topPct - 42, leftPct - 50) * 180) / Math.PI;
                  return (
                    <TutorialArrow
                      key={`tutorial-arrow-${index}`}
                      angleDeg={angleDeg}
                      delay={index * 0.08}
                    />
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
          {bubbleCards.map((card, index) => (
            <SoundBubble
              key={`${card.id ?? index}-${index}`}
              term={card.front?.trim() ?? ""}
              termLang={card.frontLang ?? deckLang ?? "en"}
              isCorrect={targetCard ? card.id === targetCard.id : false}
              isPopping={poppingIndex === index}
              isWrong={wrongIndex === index}
              showPulse={pulsingBubbleIndex === index}
              onPulseComplete={() => setPulsingBubbleIndex(null)}
              onHoverStart={() =>
                handleBubbleHoverStart(
                  card.front?.trim() ?? "",
                  card.frontLang ?? deckLang ?? "en",
                  card
                )
              }
              onHoverEnd={() => {}}
              onClick={() => handleBubbleClick(index, card)}
              index={index}
              total={bubbleCards.length}
            />
          ))}
        </div>

        <motion.div
          className="relative z-10 w-full max-w-xl mx-auto px-6 py-6 text-center"
          layout
          initial={false}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <p className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-2">
            {t("soundClouds.findTheSound")}
          </p>
          <motion.p
            key={targetTranslation || "empty"}
            className="text-2xl sm:text-3xl font-semibold text-white/95 drop-shadow-lg"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {targetTranslation || "—"}
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
}
